import React from 'react';
import { Badge, Card } from '@/components/ui';
import type { MaintenancePlan } from '../types/maintenance-plan.types';
import { format } from 'date-fns';
import { vi } from 'date-fns/locale';

interface PlanMobileListProps {
  data: MaintenancePlan[];
  isLoading: boolean;
  onRowClick: (plan: MaintenancePlan) => void;
}

export const PlanMobileList: React.FC<PlanMobileListProps> = ({ data, isLoading, onRowClick }) => {
  if (isLoading) {
    return (
      <div className="space-y-3 p-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-40 bg-slate-100 animate-pulse rounded-2xl" />
        ))}
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4">
        <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-4 text-slate-300">
          <i className="ri-calendar-todo-line text-3xl"></i>
        </div>
        <p className="text-slate-500 font-medium">Không có kế hoạch nào</p>
      </div>
    );
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active': return <Badge variant="success">Đang chạy</Badge>;
      case 'draft': return <Badge variant="neutral">Nháp</Badge>;
      case 'suspended': return <Badge variant="warning">Tạm ngưng</Badge>;
      default: return <Badge variant="neutral">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-3 p-4 pb-20">
      {data.map((plan) => (
        <Card
          key={plan.id}
          className="p-4 active:scale-[0.98] transition-transform cursor-pointer overflow-hidden relative"
          onClick={() => onRowClick(plan)}
        >
          {/* Progress bar at the top of the card */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-slate-100">
            <div
              className="h-full bg-brand-500 transition-all duration-500"
              style={{ width: `${plan.completion_rate}%` }}
            />
          </div>

          <div className="flex justify-between items-start mb-2 mt-1">
            <span className="text-[10px] font-bold text-slate-400  tracking-tight">{plan.code}</span>
            {getStatusBadge(plan.status_code)}
          </div>

          <h4 className="font-bold text-slate-800 mb-1 leading-tight">{plan.name}</h4>

          <div className="flex items-center gap-2 mb-3">
            <i className="ri-time-line text-slate-400 text-xs" />
            <span className="text-xs text-slate-500 font-medium capitalize">{plan.frequency_type}</span>
            <span className="text-slate-200">|</span>
            <span className="text-xs text-slate-600 font-bold">{plan.completion_rate}% hoàn thành</span>
          </div>

          <div className="grid grid-cols-2 gap-2 pt-3 border-t border-slate-50">
            <div className="flex flex-col">
              <span className="text-[10px] text-slate-400  font-bold">Bắt đầu</span>
              <span className="text-xs font-semibold text-slate-700">
                {plan.start_date ? format(new Date(plan.start_date), 'dd/MM/yyyy', { locale: vi }) : '---'}
              </span>
            </div>
            <div className="flex flex-col">
              <span className="text-[10px] text-slate-400  font-bold">Số đầu việc</span>
              <span className="text-xs font-semibold text-slate-700">{plan.work_order_count} công việc</span>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
};
